import * as React from 'react';
import { Button, Icon, Input, Modal, TextBox, ToggleInput } from '../..';
import { CashRegister } from '../../../context/storeContext';
import useValidator from '../../../hooks/useValidator';

type ModalProps = {
  onCloseModal: () => void;
  onAddRegister: (newRegister: {
    name: string;
    description: string;
    isPrimary: boolean;
  }) => Promise<unknown>;
  cashRegister?: CashRegister;
};

function RegisterAdd({
  onCloseModal,
  onAddRegister,
  cashRegister,
}: ModalProps) {
  const [register, setRegister] = React.useState({
    name: cashRegister?.name || '',
    description: cashRegister?.description || '',
    isPrimary: cashRegister ? cashRegister.isPrimary : false,
  });
  const [status, setStatus] = React.useState<
    'IDLE' | 'LOADING' | 'RESOLVED' | 'REJECTED'
  >('IDLE');
  const [validator, showValidationMessage] = useValidator({});

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    setRegister(fndRegister => ({
      ...fndRegister,
      [name]: value,
    }));
  };

  const handleRegisterAdd = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    setStatus('LOADING');

    await onAddRegister(register);

    setStatus('RESOLVED');
  };

  return (
    <Modal onClose={onCloseModal} show className="tablet:w-[500px]">
      <div className="rounded-md bg-white">
        <form>
          <div className="flex flex-col p-5">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre"
            >
              {!cashRegister ? 'Add New Register' : 'Update Register'}
            </h3>
            <p className="mb-2">
              <span
                className="inline-flex items-center rounded-[4px] text-xbase pl-2 pr-1 py-2 font-light 
              bg-[#ddf4ff] text-pos-blue leading-normal"
              >
                <Icon
                  id="iconHintHelp"
                  className="w-[17px] h-[17px] mr-2 shrink-0"
                />{' '}
                {` Add a register for each payment counter to help accurately report
              on sales performance, payments and more.`}
              </span>
            </p>

            <div className="text-pos-black text-sm mt-2 mb-2 font-normal space-y-4">
              <div>
                <label htmlFor="name" className="text-xbase block mb-1">
                  Name{' '}
                  <span className="text-xs font-light text-pos-red">*</span>
                </label>
                <Input
                  id="name"
                  name="name"
                  value={register.name}
                  onChange={handleInputChange}
                />
                {validator.message('name', register.name, 'required|min:3', {
                  messages: {
                    required: 'Cash register name is required.',
                  },
                  className:
                    'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                })}
              </div>

              <div>
                <label htmlFor="description" className="text-xbase block mb-1">
                  Description
                </label>
                <TextBox
                  id="description"
                  name="description"
                  value={register.description}
                  rows={3}
                  onChange={handleInputChange}
                />
              </div>

              {!(cashRegister && cashRegister.isPrimary) && (
                <div>
                  <label htmlFor="isPrimary" className="text-xbase block mb-1">
                    Make as Default
                  </label>
                  <ToggleInput
                    id="isPrimary"
                    name="isPrimary"
                    value={register.isPrimary.toString()}
                    checked={register.isPrimary}
                    onChange={event =>
                      setRegister(fndRegister => ({
                        ...fndRegister,
                        isPrimary: event.target.checked,
                      }))
                    }
                    className="align-middle cursor-pointer shrink-0"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="w-full text-right flex flex-row justify-center tablet:justify-end ml-auto space-x-4 bg-pos-yellow px-5 py-7 rounded-b-md">
            <Button
              label={!cashRegister ? 'Save' : 'Update'}
              onClick={handleRegisterAdd}
              type="button"
              disabled={status === 'LOADING'}
              loading={status === 'LOADING'}
              className="!bg-pos-black border border-[rgba(27,31,36,0.15)] min-w-[150px]  h-[40px]
              items-center justify-center !text-pos-yellow text-xbase shrink-0 font-medium 
              shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)]"
            />
          </div>
        </form>
      </div>
    </Modal>
  );
}

RegisterAdd.defaultProps = {
  cashRegister: undefined,
};

export default RegisterAdd;
